import React, { useState, useEffect, useCallback } from 'react'
import { Header } from '../components/layout/Header'

interface Annotation {
  id: string
  entity_type: string
  entity_id: string
  annotation_type: string
  content: string
  user_id: string
  user_name: string
  visibility: string
  tags: string[]
  mentions: string[]
  pinned: boolean
  resolved: boolean
  created_at: string
  updated_at: string
  reply_count: number
  reactions: Record<string, string[]>
  replies?: Annotation[]
}

interface TagStats {
  tag: string
  count: number
}

interface Statistics {
  total_annotations: number
  total_replies: number
  total_reactions: number
  by_type: Record<string, number>
  by_visibility: Record<string, number>
  by_entity_type: Record<string, number>
  total_tags: number
  popular_tags: TagStats[]
  active_users: number
}

const API_BASE = '/api/annotations'

const ANNOTATION_TYPES = [
  { value: 'comment', label: 'Comment', color: '#6B7280', icon: '💬' },
  { value: 'highlight', label: 'Highlight', color: '#F59E0B', icon: '✨' },
  { value: 'note', label: 'Note', color: '#3B82F6', icon: '📝' },
  { value: 'question', label: 'Question', color: '#8B5CF6', icon: '❓' },
  { value: 'insight', label: 'Insight', color: '#10B981', icon: '💡' },
  { value: 'action_item', label: 'Action Item', color: '#EF4444', icon: '✅' },
  { value: 'warning', label: 'Warning', color: '#F97316', icon: '⚠️' },
]

const REACTION_EMOJIS = ['👍', '👎', '❤️', '🎉', '🤔', '👀', '🚀', '💯']

const AnnotationsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'browse' | 'create' | 'search' | 'stats'>('browse')
  const [annotations, setAnnotations] = useState<Annotation[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [popularTags, setPopularTags] = useState<TagStats[]>([])
  const [loading, setLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<Annotation[]>([])

  // Browse filters
  const [entityType, setEntityType] = useState('market')
  const [entityId, setEntityId] = useState('uk')
  const [filterType, setFilterType] = useState('')
  const [includeResolved, setIncludeResolved] = useState(true)

  // Create form
  const [newAnnotation, setNewAnnotation] = useState({
    entity_type: 'market',
    entity_id: 'uk',
    annotation_type: 'comment',
    content: '',
    visibility: 'team',
    tags: '',
    mentions: '',
    pinned: false
  })

  // Reply state
  const [replyingTo, setReplyingTo] = useState<string | null>(null)
  const [replyContent, setReplyContent] = useState('')

  // Mock current user
  const currentUser = { id: 'user_1', name: 'John Smith' }

  const fetchAnnotations = useCallback(async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams({
        user_id: currentUser.id,
        include_resolved: String(includeResolved),
        limit: '50'
      })
      if (filterType) params.set('annotation_type', filterType)

      const res = await fetch(`${API_BASE}/entity/${entityType}/${entityId}?${params}`)
      if (res.ok) {
        setAnnotations(await res.json())
      }
    } catch (err) {
      console.error('Error fetching annotations:', err)
    } finally {
      setLoading(false)
    }
  }, [entityType, entityId, filterType, includeResolved])

  const fetchStatistics = async () => {
    try {
      const [statsRes, tagsRes] = await Promise.all([
        fetch(`${API_BASE}/statistics`),
        fetch(`${API_BASE}/tags/popular?limit=15`)
      ])

      if (statsRes.ok) setStatistics(await statsRes.json())
      if (tagsRes.ok) setPopularTags(await tagsRes.json())
    } catch (err) {
      console.error('Error fetching statistics:', err)
    }
  }

  useEffect(() => {
    if (activeTab === 'browse') {
      fetchAnnotations()
    } else if (activeTab === 'stats') {
      fetchStatistics()
    }
  }, [activeTab, fetchAnnotations])

  const handleSearch = async () => {
    if (!searchQuery.trim()) return
    setLoading(true)
    try {
      const res = await fetch(`${API_BASE}/search?q=${encodeURIComponent(searchQuery)}`)
      if (res.ok) {
        setSearchResults(await res.json())
      }
    } catch (err) {
      console.error('Search error:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = async () => {
    if (!newAnnotation.content.trim()) return
    setLoading(true)
    try {
      const res = await fetch(API_BASE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newAnnotation,
          user_id: currentUser.id,
          user_name: currentUser.name,
          tags: newAnnotation.tags ? newAnnotation.tags.split(',').map(t => t.trim()) : [],
          mentions: newAnnotation.mentions ? newAnnotation.mentions.split(',').map(m => m.trim()) : []
        })
      })

      if (res.ok) {
        setNewAnnotation({
          entity_type: 'market',
          entity_id: 'uk',
          annotation_type: 'comment',
          content: '',
          visibility: 'team',
          tags: '',
          mentions: '',
          pinned: false
        })
        setActiveTab('browse')
        fetchAnnotations()
      }
    } catch (err) {
      console.error('Create error:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleReply = async (parentId: string) => {
    if (!replyContent.trim()) return
    try {
      const parent = annotations.find(a => a.id === parentId)
      const res = await fetch(API_BASE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entity_type: parent?.entity_type || entityType,
          entity_id: parent?.entity_id || entityId,
          annotation_type: 'comment',
          content: replyContent,
          user_id: currentUser.id,
          user_name: currentUser.name,
          visibility: 'team',
          parent_id: parentId
        })
      })

      if (res.ok) {
        setReplyingTo(null)
        setReplyContent('')
        fetchAnnotations()
      }
    } catch (err) {
      console.error('Reply error:', err)
    }
  }

  const handleReaction = async (annotationId: string, emoji: string) => {
    try {
      await fetch(`${API_BASE}/${annotationId}/reactions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: currentUser.id, emoji })
      })
      fetchAnnotations()
    } catch (err) {
      console.error('Reaction error:', err)
    }
  }

  const handleToggleResolved = async (annotationId: string, currentResolved: boolean) => {
    try {
      await fetch(`${API_BASE}/${annotationId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: currentUser.id, resolved: !currentResolved })
      })
      fetchAnnotations()
    } catch (err) {
      console.error('Toggle resolved error:', err)
    }
  }

  const handleDelete = async (annotationId: string) => {
    if (!confirm('Are you sure you want to delete this annotation?')) return
    try {
      await fetch(`${API_BASE}/${annotationId}?user_id=${currentUser.id}`, {
        method: 'DELETE'
      })
      fetchAnnotations()
    } catch (err) {
      console.error('Delete error:', err)
    }
  }

  const getTypeInfo = (type: string) => {
    return ANNOTATION_TYPES.find(t => t.value === type) || ANNOTATION_TYPES[0]
  }

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }

  const styles: Record<string, React.CSSProperties> = {
    container: { minHeight: '100vh', backgroundColor: '#f5f7fa' },
    content: { padding: '32px', maxWidth: '1200px', margin: '0 auto' },
    title: { fontSize: '28px', fontWeight: 700, color: '#1F2937', marginBottom: '8px' },
    subtitle: { color: '#6B7280', marginBottom: '24px' },
    tabs: { display: 'flex', gap: '8px', marginBottom: '24px', borderBottom: '1px solid #E5E7EB', paddingBottom: '16px' },
    tab: { padding: '10px 20px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: 500, backgroundColor: '#F3F4F6', color: '#6B7280' },
    tabActive: { backgroundColor: '#4A90E2', color: 'white' },
    card: { backgroundColor: 'white', borderRadius: '12px', padding: '24px', marginBottom: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
    annotationCard: { backgroundColor: 'white', borderRadius: '12px', padding: '20px', marginBottom: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', borderLeft: '4px solid #4A90E2' },
    annotationHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' },
    userInfo: { display: 'flex', alignItems: 'center', gap: '12px' },
    avatar: { width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#4A90E2', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600 },
    userName: { fontWeight: 600, color: '#1F2937' },
    timestamp: { fontSize: '12px', color: '#9CA3AF' },
    typeBadge: { display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 12px', borderRadius: '9999px', fontSize: '12px', fontWeight: 500 },
    content_text: { color: '#374151', lineHeight: '1.6', marginBottom: '16px' },
    tags: { display: 'flex', gap: '8px', flexWrap: 'wrap' as const, marginBottom: '12px' },
    tag: { padding: '4px 10px', backgroundColor: '#F3F4F6', borderRadius: '6px', fontSize: '12px', color: '#6B7280' },
    actions: { display: 'flex', gap: '12px', alignItems: 'center', paddingTop: '12px', borderTop: '1px solid #F3F4F6' },
    actionButton: { padding: '6px 12px', borderRadius: '6px', border: '1px solid #E5E7EB', backgroundColor: 'white', cursor: 'pointer', fontSize: '13px', color: '#6B7280' },
    reactions: { display: 'flex', gap: '8px', flexWrap: 'wrap' as const },
    reaction: { display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 8px', backgroundColor: '#F3F4F6', borderRadius: '9999px', fontSize: '13px', cursor: 'pointer' },
    reactionPicker: { display: 'flex', gap: '4px', backgroundColor: 'white', padding: '8px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' },
    form: { display: 'flex', flexDirection: 'column' as const, gap: '16px' },
    formRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
    formGroup: { display: 'flex', flexDirection: 'column' as const, gap: '6px' },
    label: { fontSize: '14px', fontWeight: 500, color: '#374151' },
    input: { padding: '10px 14px', borderRadius: '8px', border: '1px solid #D1D5DB', fontSize: '14px' },
    select: { padding: '10px 14px', borderRadius: '8px', border: '1px solid #D1D5DB', fontSize: '14px', backgroundColor: 'white' },
    textarea: { padding: '12px 14px', borderRadius: '8px', border: '1px solid #D1D5DB', fontSize: '14px', minHeight: '120px', resize: 'vertical' as const },
    submitButton: { padding: '12px 24px', backgroundColor: '#4A90E2', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' },
    filters: { display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' as const, alignItems: 'center' },
    filterSelect: { padding: '8px 12px', borderRadius: '8px', border: '1px solid #D1D5DB', fontSize: '13px', backgroundColor: 'white' },
    searchBox: { display: 'flex', gap: '12px', marginBottom: '20px' },
    searchInput: { flex: 1, padding: '12px 16px', borderRadius: '8px', border: '1px solid #D1D5DB', fontSize: '14px' },
    searchButton: { padding: '12px 24px', backgroundColor: '#4A90E2', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 500 },
    statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' },
    statCard: { backgroundColor: 'white', borderRadius: '12px', padding: '20px', textAlign: 'center' as const },
    statValue: { fontSize: '32px', fontWeight: 700, color: '#1F2937' },
    statLabel: { fontSize: '13px', color: '#6B7280', marginTop: '4px' },
    replyBox: { marginTop: '12px', padding: '12px', backgroundColor: '#F9FAFB', borderRadius: '8px' },
    replyInput: { width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #D1D5DB', fontSize: '14px', marginBottom: '8px' },
    replyActions: { display: 'flex', gap: '8px' },
    pinnedBadge: { backgroundColor: '#FEF3C7', color: '#D97706', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 500 },
    resolvedBadge: { backgroundColor: '#D1FAE5', color: '#059669', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 500 }
  }

  const renderAnnotation = (annotation: Annotation, isReply = false) => {
    const typeInfo = getTypeInfo(annotation.annotation_type)

    return (
      <div
        key={annotation.id}
        style={{
          ...styles.annotationCard,
          borderLeftColor: typeInfo.color,
          marginLeft: isReply ? '40px' : 0,
          opacity: annotation.resolved ? 0.7 : 1
        }}
      >
        <div style={styles.annotationHeader}>
          <div style={styles.userInfo}>
            <div style={styles.avatar}>
              {annotation.user_name.charAt(0)}
            </div>
            <div>
              <div style={styles.userName}>{annotation.user_name}</div>
              <div style={styles.timestamp}>{formatDate(annotation.created_at)}</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {annotation.pinned && <span style={styles.pinnedBadge}>Pinned</span>}
            {annotation.resolved && <span style={styles.resolvedBadge}>Resolved</span>}
            <span style={{ ...styles.typeBadge, backgroundColor: `${typeInfo.color}20`, color: typeInfo.color }}>
              {typeInfo.icon} {typeInfo.label}
            </span>
          </div>
        </div>

        <div style={styles.content_text}>{annotation.content}</div>

        {annotation.tags.length > 0 && (
          <div style={styles.tags}>
            {annotation.tags.map((tag, idx) => (
              <span key={idx} style={styles.tag}>#{tag}</span>
            ))}
          </div>
        )}

        {Object.keys(annotation.reactions || {}).length > 0 && (
          <div style={styles.reactions}>
            {Object.entries(annotation.reactions).map(([emoji, users]) => (
              <span
                key={emoji}
                style={styles.reaction}
                onClick={() => handleReaction(annotation.id, emoji)}
              >
                {emoji} {users.length}
              </span>
            ))}
          </div>
        )}

        <div style={styles.actions}>
          <div style={styles.reactionPicker}>
            {REACTION_EMOJIS.map(emoji => (
              <span
                key={emoji}
                style={{ cursor: 'pointer', padding: '2px' }}
                onClick={() => handleReaction(annotation.id, emoji)}
              >
                {emoji}
              </span>
            ))}
          </div>

          {!isReply && (
            <button
              style={styles.actionButton}
              onClick={() => setReplyingTo(replyingTo === annotation.id ? null : annotation.id)}
            >
              Reply ({annotation.reply_count})
            </button>
          )}

          <button
            style={styles.actionButton}
            onClick={() => handleToggleResolved(annotation.id, annotation.resolved)}
          >
            {annotation.resolved ? 'Reopen' : 'Resolve'}
          </button>

          {annotation.user_id === currentUser.id && (
            <button
              style={{ ...styles.actionButton, color: '#EF4444' }}
              onClick={() => handleDelete(annotation.id)}
            >
              Delete
            </button>
          )}
        </div>

        {replyingTo === annotation.id && (
          <div style={styles.replyBox}>
            <input
              type="text"
              value={replyContent}
              onChange={e => setReplyContent(e.target.value)}
              placeholder="Write a reply..."
              style={styles.replyInput}
            />
            <div style={styles.replyActions}>
              <button
                style={{ ...styles.actionButton, backgroundColor: '#4A90E2', color: 'white', border: 'none' }}
                onClick={() => handleReply(annotation.id)}
              >
                Send Reply
              </button>
              <button
                style={styles.actionButton}
                onClick={() => {
                  setReplyingTo(null)
                  setReplyContent('')
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {annotation.replies && annotation.replies.length > 0 && (
          <div style={{ marginTop: '16px' }}>
            {annotation.replies.map(reply => renderAnnotation(reply, true))}
          </div>
        )}
      </div>
    )
  }

  return (
    <div style={styles.container}>
      <Header />
      <div style={styles.content}>
        <h1 style={styles.title}>Collaborative Annotations</h1>
        <p style={styles.subtitle}>Add comments, insights, and notes to data across the platform</p>

        <div style={styles.tabs}>
          {(['browse', 'create', 'search', 'stats'] as const).map(tab => (
            <button
              key={tab}
              style={{ ...styles.tab, ...(activeTab === tab ? styles.tabActive : {}) }}
              onClick={() => setActiveTab(tab)}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {activeTab === 'browse' && (
          <>
            <div style={styles.filters}>
              <select
                value={entityType}
                onChange={e => setEntityType(e.target.value)}
                style={styles.filterSelect}
              >
                <option value="market">Markets</option>
                <option value="report">Reports</option>
                <option value="seller">Sellers</option>
                <option value="category">Categories</option>
              </select>

              <select
                value={entityId}
                onChange={e => setEntityId(e.target.value)}
                style={styles.filterSelect}
              >
                {entityType === 'market' && (
                  <>
                    <option value="uk">United Kingdom</option>
                    <option value="de">Germany</option>
                    <option value="fr">France</option>
                    <option value="es">Spain</option>
                  </>
                )}
                {entityType === 'report' && (
                  <>
                    <option value="q4-2024">Q4 2024 Report</option>
                    <option value="q3-2024">Q3 2024 Report</option>
                  </>
                )}
                {entityType === 'seller' && (
                  <>
                    <option value="seller_123">TechGadgets Pro</option>
                    <option value="seller_456">Fashion Forward</option>
                  </>
                )}
                {entityType === 'category' && (
                  <>
                    <option value="electronics">Electronics</option>
                    <option value="fashion">Fashion</option>
                  </>
                )}
              </select>

              <select
                value={filterType}
                onChange={e => setFilterType(e.target.value)}
                style={styles.filterSelect}
              >
                <option value="">All Types</option>
                {ANNOTATION_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>

              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#6B7280' }}>
                <input
                  type="checkbox"
                  checked={includeResolved}
                  onChange={e => setIncludeResolved(e.target.checked)}
                />
                Include Resolved
              </label>

              <button
                onClick={fetchAnnotations}
                style={{ ...styles.actionButton, marginLeft: 'auto' }}
              >
                Refresh
              </button>
            </div>

            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                Loading annotations...
              </div>
            ) : annotations.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                <p>No annotations found for this entity.</p>
                <button
                  style={{ ...styles.submitButton, marginTop: '16px' }}
                  onClick={() => setActiveTab('create')}
                >
                  Create First Annotation
                </button>
              </div>
            ) : (
              annotations.map(ann => renderAnnotation(ann))
            )}
          </>
        )}

        {activeTab === 'create' && (
          <div style={styles.card}>
            <h2 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '20px' }}>Create New Annotation</h2>
            <div style={styles.form}>
              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Entity Type</label>
                  <select
                    value={newAnnotation.entity_type}
                    onChange={e => setNewAnnotation({ ...newAnnotation, entity_type: e.target.value })}
                    style={styles.select}
                  >
                    <option value="market">Market</option>
                    <option value="report">Report</option>
                    <option value="seller">Seller</option>
                    <option value="category">Category</option>
                  </select>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Entity ID</label>
                  <input
                    type="text"
                    value={newAnnotation.entity_id}
                    onChange={e => setNewAnnotation({ ...newAnnotation, entity_id: e.target.value })}
                    placeholder="e.g., uk, de, q4-2024"
                    style={styles.input}
                  />
                </div>
              </div>

              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Annotation Type</label>
                  <select
                    value={newAnnotation.annotation_type}
                    onChange={e => setNewAnnotation({ ...newAnnotation, annotation_type: e.target.value })}
                    style={styles.select}
                  >
                    {ANNOTATION_TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.icon} {t.label}</option>
                    ))}
                  </select>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Visibility</label>
                  <select
                    value={newAnnotation.visibility}
                    onChange={e => setNewAnnotation({ ...newAnnotation, visibility: e.target.value })}
                    style={styles.select}
                  >
                    <option value="private">Private (Only me)</option>
                    <option value="team">Team (My team)</option>
                    <option value="public">Public (Everyone)</option>
                  </select>
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Content</label>
                <textarea
                  value={newAnnotation.content}
                  onChange={e => setNewAnnotation({ ...newAnnotation, content: e.target.value })}
                  placeholder="Write your annotation here... Use @username to mention someone"
                  style={styles.textarea}
                />
              </div>

              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Tags (comma-separated)</label>
                  <input
                    type="text"
                    value={newAnnotation.tags}
                    onChange={e => setNewAnnotation({ ...newAnnotation, tags: e.target.value })}
                    placeholder="e.g., growth, uk, quarterly"
                    style={styles.input}
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Mentions (comma-separated user IDs)</label>
                  <input
                    type="text"
                    value={newAnnotation.mentions}
                    onChange={e => setNewAnnotation({ ...newAnnotation, mentions: e.target.value })}
                    placeholder="e.g., user_1, user_2"
                    style={styles.input}
                  />
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    checked={newAnnotation.pinned}
                    onChange={e => setNewAnnotation({ ...newAnnotation, pinned: e.target.checked })}
                  />
                  <span style={styles.label}>Pin this annotation</span>
                </label>
              </div>

              <button
                onClick={handleCreate}
                disabled={loading || !newAnnotation.content.trim()}
                style={{
                  ...styles.submitButton,
                  opacity: loading || !newAnnotation.content.trim() ? 0.6 : 1
                }}
              >
                {loading ? 'Creating...' : 'Create Annotation'}
              </button>
            </div>
          </div>
        )}

        {activeTab === 'search' && (
          <>
            <div style={styles.searchBox}>
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                placeholder="Search annotations..."
                style={styles.searchInput}
              />
              <button onClick={handleSearch} style={styles.searchButton}>
                Search
              </button>
            </div>

            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                Searching...
              </div>
            ) : searchResults.length > 0 ? (
              searchResults.map(ann => renderAnnotation(ann))
            ) : searchQuery ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                No results found for "{searchQuery}"
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                Enter a search term to find annotations
              </div>
            )}
          </>
        )}

        {activeTab === 'stats' && statistics && (
          <>
            <div style={styles.statsGrid}>
              <div style={styles.statCard}>
                <div style={styles.statValue}>{statistics.total_annotations}</div>
                <div style={styles.statLabel}>Total Annotations</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statValue}>{statistics.total_replies}</div>
                <div style={styles.statLabel}>Total Replies</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statValue}>{statistics.total_reactions}</div>
                <div style={styles.statLabel}>Total Reactions</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statValue}>{statistics.active_users}</div>
                <div style={styles.statLabel}>Active Users</div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
              <div style={styles.card}>
                <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>By Type</h3>
                {Object.entries(statistics.by_type).map(([type, count]) => {
                  const typeInfo = getTypeInfo(type)
                  return (
                    <div key={type} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #F3F4F6' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span>{typeInfo.icon}</span>
                        <span>{typeInfo.label}</span>
                      </span>
                      <span style={{ fontWeight: 600 }}>{count}</span>
                    </div>
                  )
                })}
              </div>

              <div style={styles.card}>
                <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>Popular Tags</h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {popularTags.map(({ tag, count }) => (
                    <span key={tag} style={{
                      ...styles.tag,
                      backgroundColor: '#DBEAFE',
                      color: '#2563EB'
                    }}>
                      #{tag} ({count})
                    </span>
                  ))}
                </div>
              </div>

              <div style={styles.card}>
                <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>By Entity Type</h3>
                {Object.entries(statistics.by_entity_type).map(([entityType, count]) => (
                  <div key={entityType} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #F3F4F6' }}>
                    <span style={{ textTransform: 'capitalize' }}>{entityType}</span>
                    <span style={{ fontWeight: 600 }}>{count}</span>
                  </div>
                ))}
              </div>

              <div style={styles.card}>
                <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>By Visibility</h3>
                {Object.entries(statistics.by_visibility).map(([visibility, count]) => (
                  <div key={visibility} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #F3F4F6' }}>
                    <span style={{ textTransform: 'capitalize' }}>{visibility}</span>
                    <span style={{ fontWeight: 600 }}>{count}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default AnnotationsPage
